import * as React from 'react';
import { Navigate, Route, useParams } from 'react-router-dom';
import { useTrackedLocation } from './hooks/useLocation';
import NotFound from '../components/PageNotFound.tsx';
import { IConditionalComponents } from '../../customizations/ConditionalComponentContext';
import { LOGIN_PATH, SETUP_MFA_PATH } from '../../login/constants.ts';
import { url } from './link.ts';
import { canSkipMfa, isChatAgentOnlyUser } from '../../users/utils.ts';

const CHAT_AGENT_ALLOWED_EXACT_PATHS = ['/'];
const CHAT_AGENT_ALLOWED_PATH_PREFIXES = [
    '/chat',
    '/user/profile',
    '/user/notifications',
    '/user/password',
    SETUP_MFA_PATH,
];

const isChatAgentAllowedPath = (pathname: string) =>
    CHAT_AGENT_ALLOWED_EXACT_PATHS.includes(pathname) ||
    CHAT_AGENT_ALLOWED_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));

export interface IFeatureFlags {
    assistanceEmailThreads?: boolean;
    highlightSelectedInDocumentSuggestions?: boolean;
}

export interface IModuleProps {
    name: string;
    routes?: IRouteProps[];
    locales?: any;
    components?: IConditionalComponents;
    featureFlags?: IFeatureFlags;
}

export interface IRouteProps {
    path: any;
    loginRequired?: boolean;
    condition?: any;
    component: any;
    children?: IRouteProps[];
}

const CustomRoute = ({ component: Component, condition, loginRequired = true, mfaRequired = true, ...routeProps }) => {
    const { user, loggedIn } = routeProps;

    const params = useParams();
    const location = useTrackedLocation();

    const redirectUrl = location.search ? `${location.pathname}${location.search}` : location.pathname;
    const search = redirectUrl && redirectUrl !== '/' ? `?next=${encodeURIComponent(redirectUrl)}` : '';

    if (
        mfaRequired &&
        loggedIn === true &&
        user?.hasMfaDevice === false &&
        user?.customer?.mfaConfig?.isRequired &&
        !canSkipMfa(user)
    ) {
        // enforce setup mfa
        return <Navigate to={{ pathname: url(SETUP_MFA_PATH), search }} />;
    }

    if (!loginRequired || loggedIn === true) {
        const chatAgentBlocked =
            loggedIn === true && isChatAgentOnlyUser(user) && !isChatAgentAllowedPath(location.pathname);
        return !chatAgentBlocked && (!condition || condition(routeProps)) ? (
            <Component params={params} {...routeProps} />
        ) : (
            <NotFound />
        );
    }

    return <Navigate to={{ pathname: url(LOGIN_PATH), search }} />;
};

class Modules {
    private modules = {};

    public populate(modules: IModuleProps[]) {
        modules.forEach(({ name, ...options }) => this.register(name, options));
    }

    public register(name, options) {
        const { routes = [], locales = {} } = options;

        this.modules[name] = {
            name,
            routes,
            locales,
        } as IModuleProps;
    }

    public getRoutes() {
        return Object.values(this.modules).flatMap((mod: IModuleProps) => mod.routes);
    }

    public renderRoutes(props) {
        // Specific output for ReactRouter
        return this.getRoutes().map(({ children, ...route }: IRouteProps) => {
            // @ts-ignore
            const { path, ...routeProps } = route;
            return (
                <Route key={path} path={path} element={<CustomRoute {...props} {...routeProps} />}>
                    {children &&
                        children.map((child) => (
                            <Route key={child.path} path={child.path} element={<CustomRoute {...props} {...child} />} />
                        ))}
                </Route>
            );
        });
    }

    public getLocales() {
        // Specific output for i18n-next resource bundles
        const locales = {};

        Object.values(this.modules).forEach((mod: IModuleProps) => {
            Object.entries(mod.locales).forEach(([locale, translations]) => {
                if (!(locale in locales)) locales[locale] = {};
                locales[locale][mod.name] = translations;
            });
        });

        return locales;
    }
}

// create default module registry
export const modules = new Modules();
